import { FormControl, Validators } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormsValidators } from '../components/forms/validators/forms.validators';
import { IInputOption } from '../components/formly';
import { BtlType, EmploymentStatus, IUiMainIncome, ProductLength } from '../models';
import { enumKeys } from './enum-helpers';
import { toSentenceCase } from './format-helpers';

export function defaultSelectOption(): IInputOption {
  return { value: undefined, label: 'Please select...', disabled: true };
}

export function yesNoOptions(): (IInputOption & { srOnly: boolean })[] {
  return [
    { value: true, icon: 'check', label: 'Yes', srOnly: true },
    { value: false, icon: 'times', label: 'No', srOnly: true },
  ];
}

export function numberInput(
  key: string,
  text: string,
  tooltip: string = null,
  hideExpr: ((field: FormlyFieldConfig) => any) = null,
  validators: any[] = null,
  expressions: {
    [property: string]: (model: any, formState: any, field?: FormlyFieldConfig) => any;
  } = null,
  extraValidators: any = {},
  className: string = null,
  addonStart = null,
  addonEnd = null,
  props = {},
  type: string = null,
  defaultValue: number = null,
  displayZeroIfDefaultValueIsNull: boolean = true,
  extraOptions: Partial<FormlyFieldConfig> = {},
) {
  const field = {
    key,
    type: type ?? 'input',
    defaultValue: displayZeroIfDefaultValueIsNull ? defaultValue ?? '0' : defaultValue,
    className,
    props: {
      text,
      type: 'number',
      tooltip,
      required: true,
      min: 0,
      addonEnd: addonEnd,
      addonStart: addonStart,
      ...props,

      // Merge the blur + focus events with ones that may be provided in template options so that adding a blur/focus
      // event to an input in the field setup does not inadvertently remove this functionality.
      focus: (field: FormlyFieldConfig, event: any) => {
        const { value } = event.target;
        const valid = FormsValidators.greaterThanZero(value);

        if (!valid) {
          event.target.value = '';
        }

        if (props) {
          if (typeof props['focus'] === 'function') {
            props['focus'](field, event);
          }
        }
      },
      blur: (field: FormlyFieldConfig, event: any) => {
        const { value } = event.target;
        const valid = FormsValidators.greaterThanZero(value);
        if (!valid) {
          event.target.value = 0;
          field.formControl.setValue(0);
        }

        if (props) {
          if (typeof props['blur'] === 'function') {
            props['blur'](field, event);
          }
        }
      },
    },
    validators: {
      validation: validators ?? [FormsValidators.integer],
      ...extraValidators,
    },
    expressions: {
      hide: hideExpr,
      ...expressions,
    },
    ...extraOptions,
  } as FormlyFieldConfig;

  return field;
}

export function radioInput(
  key: string,
  text: string,
  tooltip: string = null,
  hideExpr: ((field: FormlyFieldConfig) => any) = null,
  options: IInputOption[] = null,
  expressions: {
    [property: string]: (model: any, formState: any, field?: FormlyFieldConfig) => any;
  } = null,
  defaultValue = null,
  className: string = null,
  props = {},
  extraOptions: Partial<FormlyFieldConfig> = {},
  validators: any[] = [],
) {
  return {
    key,
    type: 'customRadio',
    defaultValue,
    className,
    props: {
      text,
      tooltip,
      options: options ?? yesNoOptions(),
      required: true,
      ...props,
      validators,
    },
    expressions: {
      hide: hideExpr,
      ...expressions,
    },
    ...extraOptions,
  } as FormlyFieldConfig;
}

export function currencyInput(
  key: string,
  text: string,
  tooltip: string = null,
  hideExpr: ((field: FormlyFieldConfig) => any) = null,
  validators: any[] = [],
  expressions: {
    [property: string]: (model: any, formState: any, field?: FormlyFieldConfig) => any;
  } = null,
  extraValidators: any = {},
  className: string = null,
  props = {},
  defaultValue: number = null,
  displayZeroIfDefaultValueIsNull: boolean = true,
  extraOptions: Partial<FormlyFieldConfig> = {},
) {
  return numberInput(
    key,
    text,
    tooltip,
    hideExpr,
    validators,
    expressions,
    extraValidators,
    className,
    '£',
    null,
    props,
    'currency',
    defaultValue,
    displayZeroIfDefaultValueIsNull,
    extraOptions,
  );
}

export function multipleBenefitsInput(
  key: string,
  text: string,
  tooltip: string = null,
  hideExpr: ((field: FormlyFieldConfig) => any) = null,
  validators: any[] = [],
  expressions: {
    [property: string]: (model: any, formState: any, field?: FormlyFieldConfig) => any;
  } = null,
  extraValidators: any = {},
  className: string = null,
  props = {},
  fieldName: string,
  addonStart: string,
  addonEnd: string,
) {

  const field = {
    key,
    type: 'currency',
    defaultValue: 0,
    className,
    fieldName,
    props: {
      text,
      type: 'number',
      tooltip,
      required: true,
      min: 0,
      addonEnd: addonEnd,
      addonStart: addonStart,
      ...props,

      // Merge the blur + focus events with ones that may be provided in template options so that adding a blur/focus
      // event to an input in the field setup does not inadvertently remove this functionality.
      focus: (field: FormlyFieldConfig, event: any) => {
        const { value } = event.target;
        const valid = FormsValidators.greaterThanZero(value);

        if (!valid) {
          event.target.value = '';
        }

        if (props) {
          if (typeof props['focus'] === 'function') {
            props['focus'](field, event);
          }
        }
      },
      blur: (field: FormlyFieldConfig, event: any) => {
        const { value } = event.target;
        const valid = FormsValidators.greaterThanZero(value);
        if (!valid) {
          event.target.value = 0;
          field.formControl.setValue(0);
        }

        if (props) {
          if (typeof props['blur'] === 'function') {
            props['blur'](field, event);
          }
        }
      },
    },
    validators: {
      validation: validators ?? [FormsValidators.integer],
      ...extraValidators,
    },
    expressions: {
      hide: hideExpr,
      ...expressions,
    },
  } as FormlyFieldConfig;

  return field;
}

export function selectInput(
  key: string,
  text: string,
  tooltip: string = null,
  hideExpr: ((field: FormlyFieldConfig) => any) = null,
  options: IInputOption[] = null,
  expressions: {
    [property: string]: (model: any, formState: any, field?: FormlyFieldConfig) => any;
  } = null,
  defaultValue = undefined,
  className: string = null,
  props = {},
  extraOptions: Partial<FormlyFieldConfig> = {},
) {
  return {
    key,
    type: 'select',
    defaultValue,
    className,
    props: {
      text,
      tooltip,
      options,
      required: true,
      ...props,
    },
    expressions: {
      hide: hideExpr,
      ...expressions,
    },
    ...extraOptions,
  } as FormlyFieldConfig;
}

export function hiddenInput(
  key: string,
  hideExpr: ((field: FormlyFieldConfig) => any) = null,
) {
  return {
    key,
    type: 'input',
    props: {
      type: 'hidden',
      inputType: 'hidden',
    },
    expressions: {
      hideExpr,
    },
  } as FormlyFieldConfig;
}

export function buildOptionsFromEnum(enumeration: any, convertToLowerCase: boolean = true) {
  const propertyNames = enumKeys(enumeration);
  return propertyNames.map((prop) => {
    let optionText = toSentenceCase(prop, convertToLowerCase);
    if (prop === 'RentalIncome') {
      optionText = 'Rental income (Mortgage Free)';
    }
    return {
      value: enumeration[prop],
      label: optionText,
    };
  });
}

export function buildSelectOptionsFromEnum(enumeration: any, convertToLowerCase: boolean = true) {
  return [defaultSelectOption(), ...buildOptionsFromEnum(enumeration, convertToLowerCase)] as IInputOption[];
}

export function getEnumLabel(value: number, enumeration: any, convertToLowerCase: boolean = true) {
  const types = buildOptionsFromEnum(enumeration, convertToLowerCase);
  const enumValue = types.find((s) => s.value === value);
  return enumValue.label;
}

export function getEnumValue(value: string): number {
  return +value.split(':')[0];
}

export function isEmployed(employmentStatus: EmploymentStatus): boolean {
  return (
    employmentStatus === EmploymentStatus.Employed ||
    employmentStatus === EmploymentStatus.SelfEmployed ||
    employmentStatus === EmploymentStatus.DirectorOrShareholder
  );
}

export function isUnemployed(employmentStatus: EmploymentStatus): boolean {
  return (
    employmentStatus === EmploymentStatus.Homemaker ||
    employmentStatus === EmploymentStatus.Retired ||
    employmentStatus === EmploymentStatus.Student ||
    employmentStatus === EmploymentStatus.Unemployed
  );
}

export function isHomemakerStudentOrUnemployed(employmentStatus: EmploymentStatus): boolean {
  return (
    employmentStatus === EmploymentStatus.Homemaker ||
    employmentStatus === EmploymentStatus.Student ||
    employmentStatus === EmploymentStatus.Unemployed
  );
}

export function salaryAndDividendsGreaterThanZero() {
  return {
    expression: (control: FormControl) => {
      const {
        grossSalaryForLatestPeriod,
        grossDividendsForLatestPeriod,
        grossAnnualIncome,
        employmentStatus,
      } = control.value as IUiMainIncome;
      return (
        employmentStatus !== EmploymentStatus.DirectorOrShareholder ||
        grossSalaryForLatestPeriod +
          grossDividendsForLatestPeriod +
          parseInt((grossAnnualIncome ?? 0).toString()) >
          0
      );
    },
    message:
      'Either gross annual income, director\'s salary for latest period or gross dividends for latest period' +
       ' should be greater than 0',
  };
}

export function yearMonthValidator() {
  return {
    lengthOfTime: {
      expression: (control) => {
        const { years, months } = control.value;
        return years > 0 || months > 0;
      },
      message: (error, field) => `${field.parent.props.text} must be greater than 0`,
      errorPath: 'years',
    },
  };
}

export function yearsAndMonthsFields() {
  return [
    numberInput(
      'years', null, null, null, null, null, null, 'col-md-6 mb-3 mb-md-0 pr-md-1 year-months', null, 'Years',
    ),
    numberInput(
      'months',
      null,
      null,
      null,
      [FormsValidators.integer, Validators.max(11)],
      null,
      null,
      'col-md-6 mb-3 mb-md-0 pl-md-1 year-months',
      null,
      'Months',
      {
        blur: (field: FormlyFieldConfig) => {
          // If user edits the 'months', ensure we mark 'years' as touched since that is where the
          // error messages are usually shown for yearsAndMonthsFields controls.
          field.parent.fieldGroup[0].formControl.markAsTouched();
        },
      },
    ),
  ];
}

export function productLengthOptions(): IInputOption[] {
  return [
    { value: undefined, label: 'Please select...' },
    { value: ProductLength.OneYear, label: '1 Year' },
    { value: ProductLength.TwoYears, label: '2 Years' },
    { value: ProductLength.ThreeYears, label: '3 Years' },
    { value: ProductLength.FourYears, label: '4 Years' },
    { value: ProductLength.FiveYears, label: '5 Years' },
  ];
}

export function productLengthBtlOptions(): IInputOption[] {
  return [
    { value: undefined, label: 'Please select...' },
    { value: ProductLength.TwoYears, label: '2 Years' },
    { value: ProductLength.ThreeYears, label: '3 Years' },
    { value: ProductLength.FiveYears, label: '5 Years' },
  ];
}

export function productBtlTypeOptions(): IInputOption[] {
  return [
    { value: undefined, label: 'Please select...' },
    { value: BtlType.Standard, label: 'Standard' },
    { value: BtlType.HMO, label: 'HMO' },
    { value: BtlType.MultiBlockUnit, label: 'Multi-block unit' },
    { value: BtlType.HolidayLet, label: 'Holiday Let' },
  ];
}

export function buildSelectOptionsFromRange(lowerLimit: number, upperLimit: number) {
  const values = [];
  let curValue = lowerLimit;
  while (curValue <= upperLimit) {
    values.push({ value: curValue, label: `${curValue}` });
    curValue++;
  }
  return [defaultSelectOption(), ...values] as IInputOption[];
}
